// Il disegno di una storia, sotto le sue fotografie. Il quaderno ne propone alcuni insieme e lei
// sceglie quello che le piace («Scelgo questo») o li rimanda tutti indietro («Nessuno mi piace»);
// su quello scelto: 👍 lo tiene, 👎 lo toglie, 🔄 ne chiede altri (anche in uno stile scelto).
// Un disegno arriva in qualche minuto: mentre il quaderno disegna, la carta lo dice e si riguarda
// da sola finché resta sullo schermo.
import { api } from './api.js';
import { el } from './ui.js';

type Stato = {
  disegno: { id: number; url: string; stile: string | null; didascalia: string | null; voto: number | null } | null;
  inCorso: boolean;
  nonRiuscito: 'fallito' | 'saltato' | null;
  puoiRifare: boolean;
  perVolta: number;
  domattina?: boolean;
  daScegliere: { gruppo: number; disegni: Array<{ id: number; url: string; stile: string | null; didascalia: string | null }> } | null;
  spento?: boolean;
};

const NOME_STILE: Record<string, string> = { acquerello: 'acquerello', pastello: 'pastelli', matita: 'matita', colori: 'colori vivaci' };

const STILI: Array<[string, string]> = [
  ['acquerello', '🎨 Acquerello'],
  ['pastello', '🖍️ Pastelli'],
  ['matita', '✏️ Matita, bianco e nero'],
  ['colori', '🌈 Colori vivaci'],
];

/** La carta del disegno. Torna subito un contenitore vuoto e lo riempie quando sa cosa mostrare:
 *  con i disegni spenti resta vuoto, e la storia è esattamente quella di sempre. */
export function cartaDisegno(memoryId: number): HTMLElement {
  const carta = el('section', { class: 'disegno-storia', 'data-ruolo': 'disegno', 'aria-label': 'Un disegno per questa storia' });
  let attesa: number | undefined;
  let tolto: number | null = null; // l'ultimo disegno appena tolto con 👎: si può annullare

  const base = `/api/memories/${memoryId}/disegno`;
  const carica = async (): Promise<void> => {
    try { disegna(await api.get<Stato>(base)); } catch { /* senza disegno la storia resta com'è */ }
  };
  const manda = async (url: string, body: unknown): Promise<void> => {
    try { disegna(await api.post<Stato>(url, body)); } catch { await carica(); }
  };
  const rifai = (stile?: string) => manda(`${base}/rifai`, stile ? { stile } : {});
  const sceltaStile = (fai: (stile: string) => Promise<void>): HTMLElement => el('details', { class: 'disegno-stili' },
    el('summary', {}, 'Oppure scegli tu come'),
    el('div', { class: 'disegno-stili-scelte' },
      ...STILI.map(([stile, testo]) => el('button', { class: 'btn-secondario', type: 'button', click: () => fai(stile) }, testo)),
    ),
  );
  const aspetta = (): void => { attesa = window.setTimeout(() => { if (carta.isConnected) void carica(); }, 20_000); };

  function disegna(s: Stato): void {
    window.clearTimeout(attesa);
    if (s.spento) { carta.replaceChildren(); return; }
    const pezzi: Array<HTMLElement | null> = [];

    // Più disegni insieme: sceglie lei.
    if (s.daScegliere) {
      const g = s.daScegliere;
      pezzi.push(el('p', { class: 'disegno-domanda' },
        g.disegni.length === 1 && s.inCorso
          ? 'Ecco il primo disegno per questa storia: gli altri stanno arrivando.'
          : `Ho fatto ${g.disegni.length} disegni per questa storia: quale ti piace di più?`));
      pezzi.push(el('div', { class: 'disegno-scelta' }, ...g.disegni.map((d, i) => el('figure', { class: 'disegno-candidato' },
        el('img', { src: d.url, alt: `Disegno ${i + 1}${d.didascalia ? `: ${d.didascalia}` : ''}`, loading: 'lazy', decoding: 'async' }),
        el('figcaption', {}, d.didascalia ?? `Disegno ${i + 1}`, d.stile && NOME_STILE[d.stile] ? el('span', { class: 'disegno-stile' }, ` · ${NOME_STILE[d.stile]}`) : null),
        el('button', { class: 'btn-primario disegno-scegli', type: 'button', click: () => manda(`${base}/scegli`, { disegnoId: d.id }) }, '💚  Scelgo questo'),
      ))));
      if (s.inCorso) {
        pezzi.push(el('p', { class: 'disegno-nota disegno-in-corso', role: 'status' }, '✏️  Sto finendo gli altri… Puoi già scegliere, se uno ti piace.'));
        aspetta();
      } else if (s.puoiRifare) {
        pezzi.push(el('button', { class: 'btn-secondario disegno-rifai', type: 'button', click: () => manda(`${base}/nessuno`, { gruppo: g.gruppo }) },
          '🔄  Nessuno mi piace: fanne altri'));
        pezzi.push(sceltaStile((stile) => manda(`${base}/nessuno`, { gruppo: g.gruppo, stile })));
      } else if (s.domattina) {
        pezzi.push(el('p', { class: 'disegno-nota' }, '🌅  Domattina trovi qui i disegni nuovi.'));
      } else {
        // Il tetto del giorno: mai un «no» secco, lei può sempre dire «questi no» e averne altri domattina.
        pezzi.push(el('p', { class: 'disegno-nota' }, 'Per oggi ne ho disegnati tanti: se uno di questi ti piace sceglilo, altrimenti te ne preparo altri per domattina.'));
        pezzi.push(el('button', { class: 'btn-secondario disegno-rifai', type: 'button', click: () => manda(`${base}/nessuno`, { gruppo: g.gruppo }) },
          '🌅  Nessuno mi piace: rifalli domattina'));
      }
      carta.replaceChildren(...pezzi.filter((p): p is HTMLElement => p !== null));
      return;
    }

    if (s.disegno) {
      const d = s.disegno;
      pezzi.push(el('figure', { class: 'disegno-figura' },
        el('img', { src: d.url, alt: d.didascalia ? `Disegno: ${d.didascalia}` : 'Un disegno della storia', loading: 'lazy', decoding: 'async' }),
        el('figcaption', {}, d.didascalia ?? 'Un disegno per questa storia'),
      ));
      const piace = d.voto === 1;
      pezzi.push(el('div', { class: 'disegno-voti' },
        el('button', {
          class: `disegno-voto${piace ? ' scelto' : ''}`, type: 'button', 'aria-pressed': String(piace),
          click: () => manda(`${base}/voto`, { disegnoId: d.id, voto: piace ? 0 : 1 }),
        }, piace ? '👍  Ti piace' : '👍  Mi piace'),
        el('button', {
          class: 'disegno-voto', type: 'button',
          click: async () => { tolto = d.id; await manda(`${base}/voto`, { disegnoId: d.id, voto: -1 }); },
        }, '👎  Non mi piace'),
      ));
    } else if (tolto !== null && !s.inCorso) {
      const id = tolto;
      pezzi.push(el('p', { class: 'disegno-nota' }, 'Va bene, l’ho tolto.'),
        el('button', { class: 'btn-secondario disegno-annulla', type: 'button', click: async () => { tolto = null; await manda(`${base}/voto`, { disegnoId: id, voto: 0 }); } }, 'Ci ho ripensato: rimettilo'));
    }

    if (s.inCorso) {
      pezzi.push(el('p', { class: 'disegno-nota disegno-in-corso', role: 'status' }, '✏️  Sto disegnando… ci vuole qualche minuto. Puoi leggere intanto.'));
      aspetta();
    } else if (s.nonRiuscito === 'fallito') {
      pezzi.push(el('p', { class: 'disegno-nota' }, 'Questa volta il disegno non mi è riuscito. Riprova più tardi.'));
    } else if (s.nonRiuscito === 'saltato') {
      pezzi.push(el('p', { class: 'disegno-nota' }, 'Per questa storia preferisco non fare un disegno: meglio le tue parole.'));
    }

    if (!s.inCorso && s.puoiRifare) {
      const altri = s.perVolta > 1;
      pezzi.push(el('button', { class: 'btn-secondario disegno-rifai', type: 'button', click: () => rifai() },
        s.disegno ? (altri ? '🔄  Fammene altri' : '🔄  Fammene un altro') : (altri ? '✏️  Fammi dei disegni di questa storia' : '✏️  Fammi un disegno di questa storia')));
      pezzi.push(sceltaStile((stile) => rifai(stile)));
    } else if (s.domattina) {
      pezzi.push(el('p', { class: 'disegno-nota' }, '🌅  Domattina trovi qui i disegni nuovi.'));
    } else if (!s.inCorso && !s.puoiRifare) {
      pezzi.push(el('p', { class: 'disegno-nota' }, 'Per oggi ne ho disegnati tanti: se vuoi, te ne preparo altri per domattina.'));
      pezzi.push(el('button', { class: 'btn-secondario disegno-rifai', type: 'button', click: () => manda(`${base}/rifai`, { domattina: true }) },
        '🌅  Fammene altri per domattina'));
    }

    carta.replaceChildren(...pezzi.filter((p): p is HTMLElement => p !== null));
  }

  void carica();
  return carta;
}
